import type { GQL_ID } from '@@partner:types:graphql'
import type { ProductListItem } from '@@partner:types:app'

import React, { useState, useCallback } from 'react'
import { SelectPicker, InputGroup, IconButton, Whisper, Tooltip } from 'rsuite'
import SearchIcon from '@rsuite/icons/Search'
import CopyIcon from '@rsuite/icons/Copy'
import { gql, useLazyQuery } from '@apollo/client'
import { useFetchAllProductGroups } from '@@partner:graphql/query/productGroup/allProductGroups.gql'
import { useLazyQueryProducts } from '@@partner:graphql/query/catalog/products.gql'
import { 
  buildProduct, 
  buildCatalogLvl1
} from '@hive/platform-url-schema'
import { CUSTOMER_FRONTEND_URL } from '@@partner:config'
import { notifyOk } from '@@partner:helpers/notification'

const GQL_QUERY_CATEGORIES_WITH_SLUG = gql`
  query {
    categories: allCategories(
      pager: { limit: 9999 }
    ) {
      edges {
        id
        name
        slug
        parent_id
      }
    }
  }
`

interface CategoryWithSlug {
  id: GQL_ID
  name: string
  slug?: string
  parent_id?: GQL_ID
}

interface CategoriesWithSlugQuery {
  categories: {
    edges: CategoryWithSlug[]
  }
}

type ObjectType = 'category' | 'product' | 'group'

interface ObjectLinkSelectorProps {
  onSelect: (url: string) => void
}

interface ObjectOption {
  label: string
  value: GQL_ID
  type: ObjectType
  slug?: string
  id?: GQL_ID
}

const OBJECT_TYPES: { label: string; value: ObjectType }[] = [
  { label: 'Категория', value: 'category' },
  { label: 'Товар', value: 'product' },
  { label: 'Группа', value: 'group' }
]

export default function ObjectLinkSelector ({ onSelect }: ObjectLinkSelectorProps) {
  const [ objectType, setObjectType ] = useState<ObjectType | null>(null)
  const [ selectedObject, setSelectedObject ] = useState<GQL_ID | null>(null)
  const [ categoryData, setCategoryData ] = useState<ObjectOption[]>([])
  const [ productData, setProductData ] = useState<ObjectOption[]>([])
  const [ groupData, setGroupData ] = useState<ObjectOption[]>([])
  const [ generatedUrl, setGeneratedUrl ] = useState<string>('')

  const [ fetchCategories, { loading: loadingCategories } ] = useLazyQuery<CategoriesWithSlugQuery>(GQL_QUERY_CATEGORIES_WITH_SLUG)
  const [ fetchProducts, { loading: loadingProducts } ] = useLazyQueryProducts()
  
  const { data: productGroupsData, loading: loadingGroups } = useFetchAllProductGroups({
    pager: { page: 1, limit: 100 }
  })

  const loadCategories = useCallback(async () => {
    const result = await fetchCategories()
    if (result.data?.categories.edges) {
      const categories: ObjectOption[] = result.data.categories.edges.map((cat: CategoryWithSlug) => ({
        label: cat.name,
        value: cat.id,
        type: 'category' as ObjectType,
        slug: cat.slug,
        id: cat.id
      }))
      setCategoryData(categories)
    }
  }, [ fetchCategories ])

  const searchProducts = useCallback(async (searchText: string) => {
    if (searchText.length < 2) {
      return
    }
    
    const result = await fetchProducts({
      variables: {
        pager: { page: 1, limit: 20 },
        filter: {
          or: [
            { fields: [ 'name' ], op: 'starts_with', value: [ searchText ] },
            { fields: [ 'xid' ], op: 'eq', value: [ searchText ] }
          ]
        }
      }
    })
    
    if (result.data?.products.edges) {
      const products: ObjectOption[] = result.data.products.edges.map((prod: ProductListItem) => ({
        label: `${prod.name} (${prod.xid})`,
        value: prod.id,
        type: 'product' as ObjectType,
        slug: prod.xid,
        id: prod.id
      }))
      setProductData(products)
    }
  }, [ fetchProducts ])

  const handleObjectTypeChange = useCallback((type: ObjectType | null) => {
    if (!type) {
      return
    }
    setObjectType(type)
    setSelectedObject(null)
    setGeneratedUrl('')
    
    if (type === 'category') {
      loadCategories()
    }
    else if (type === 'group' && productGroupsData) {
      const groups: ObjectOption[] = productGroupsData.allProductGroups.edges.map(group => ({
        label: group.name,
        value: group.id,
        type: 'group' as ObjectType,
        id: group.id
      }))
      setGroupData(groups)
    }
  }, [ loadCategories, productGroupsData ])

  const handleObjectSelect = useCallback((value: GQL_ID | null) => {
    if (!value) {
      return
    }
    setSelectedObject(value)
    
    let url = ''
    
    if (objectType === 'category') {
      const category = categoryData.find(c => c.value === value)
      if (category?.slug) {
        url = `${CUSTOMER_FRONTEND_URL}${buildCatalogLvl1(category.slug)}`
      }
    }
    else if (objectType === 'product') {
      const product = productData.find(p => p.value === value)
      if (product?.slug) {
        url = `${CUSTOMER_FRONTEND_URL}${buildProduct(product.slug)}`
      }
    }
    else if (objectType === 'group') {
      url = `${CUSTOMER_FRONTEND_URL}/product-groups/${value}`
    }
    
    setGeneratedUrl(url)
  }, [ objectType, categoryData, productData ])

  const handleApplyUrl = useCallback(() => {
    if (generatedUrl) {
      onSelect(generatedUrl)
      notifyOk('Ссылка применена')
    }
  }, [ generatedUrl, onSelect ])

  const handleCopyUrl = useCallback(() => {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl)
      notifyOk('Ссылка скопирована')
    }
  }, [ generatedUrl ])

  const getObjectData = (): ObjectOption[] => {
    switch (objectType) {
    case 'category': 
      return categoryData
    case 'product': 
      return productData
    case 'group': 
      return groupData
    default: 
      return []
    }
  }

  const getPlaceholder = (): string => {
    switch (objectType) {
    case 'category': 
      return 'Выберите категорию'
    case 'product': 
      return 'Введите название или XID товара'
    case 'group': 
      return 'Выберите группу'
    default: 
      return 'Выберите объект'
    }
  }

  return (
    <div className="my-3 p-3 border rounded bg-gray-50">
      <p className="text-sm text-gray-600 mb-2">Быстрый выбор ссылки на объект</p>
      <div className="flex gap-2 flex-wrap">
        <SelectPicker
          data={OBJECT_TYPES}
          value={objectType}
          onChange={handleObjectTypeChange}
          placeholder="Тип объекта"
          style={{ width: 150 }}
          cleanable={false}
        />
        
        { objectType && (
          <SelectPicker
            data={getObjectData()}
            value={selectedObject}
            onChange={handleObjectSelect}
            onSearch={objectType === 'product' ? searchProducts : undefined}
            placeholder={getPlaceholder()}
            style={{ width: 300 }}
            loading={objectType === 'category' ? loadingCategories : objectType === 'product' ? loadingProducts : objectType === 'group' ? loadingGroups : false}
          />
        )}
        
        { generatedUrl && (
          <InputGroup style={{ width: 'auto' }}>
            <InputGroup.Addon style={{ padding: '8px 12px', background: '#f8f9fa' }}>
              {generatedUrl}
            </InputGroup.Addon>
            <Whisper placement="top" trigger="click" speaker={<Tooltip>Скопировано!</Tooltip>}>
              <IconButton
                icon={<CopyIcon />}
                onClick={handleCopyUrl}
              />
            </Whisper>
            <IconButton
              icon={<SearchIcon />}
              appearance="primary"
              onClick={handleApplyUrl}
              title="Применить ссылку"
            />
          </InputGroup>
        )}
      </div>
    </div>
  )
}
